import DT from 'duration-time-conversion';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

const Metronome = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 30px;
    background-color: rgba(0, 0, 0, 0.2);
    cursor: pointer;
    pointer-events: auto !important; /* Force pointer events */
    touch-action: none; /* Disable browser handling of touch events */
    
    /* Add hover effect to indicate clickable */
    &:hover {
        background-color: rgba(0, 0, 0, 0.3);
    }
    
    /* Add active effect to indicate clicking */
    &:active {
        background-color: rgba(0, 0, 0, 0.4);
    }

    .template {
        position: absolute;
        top: 0;
        bottom: 0;
        height: 100%;
        background-color: rgba(76, 175, 80, 0.5);
        border-left: 1px solid rgba(76, 175, 80, 0.8);
        border-right: 1px solid rgba(76, 175, 80, 0.8);
        user-select: none;
        pointer-events: none;
    }

    .click-indicator {
        position: absolute;
        top: 0;
        height: 100%;
        width: 2px;
        background-color: white;
        z-index: 20;
    }
`;

function findIndex(subs, startTime) {
    // If subtitles array is empty, return 0
    if (!subs || subs.length === 0) return 0;
    
    // Find the appropriate index for insertion
    const index = subs.findIndex((item, index) => {
        // If this is the last subtitle and startTime is after it
        if (startTime >= item.endTime && !subs[index + 1]) return true;
        
        // If startTime is within this subtitle's range
        if (item.startTime <= startTime && item.endTime > startTime) return true;
        
        // If startTime is between this and the next subtitle
        if (startTime >= item.endTime && subs[index + 1] && startTime < subs[index + 1].startTime) return true;

        // If this is before the first subtitle start time 
        if (index === 0 && startTime < item.startTime) return true;
        
        return false;
    });
    
    // If no match found, place at the end
    let indexValue = index === -1 ? subs.length : index;

    indexValue = indexValue === 0? index - 1 : indexValue;

    return indexValue;
}

export default function Component({ render, subtitle, newSub, addSub, player, playing, waveform }) {
    const [isDroging, setIsDroging] = useState(false);
    const [drogStartTime, setDrogStartTime] = useState(0);
    const [drogEndTime, setDrogEndTime] = useState(0);
    const [isClicking, setIsClicking] = useState(false);
    const gridGap = document.body.clientWidth / render.gridNum;
    const metronomeRef = useRef(null);
    // Add these to track click vs. drag
    const clickStartTimeRef = useRef(null);
    const clickStartPositionRef = useRef(null);
    const DRAG_THRESHOLD = 5; // Pixels of movement to consider a drag instead of click

    const getEventTime = useCallback(
        (event) => {
            return (event.pageX - render.padding * gridGap) / gridGap / 10 + render.beginTime;
        },
        [gridGap, render],
    );

    const onMouseDown = useCallback(
        (event) => {
            if (event.button !== 0) return;
            // If this is a direct click on the metronome and not a child element
            if (event.target === metronomeRef.current) {
                setIsClicking(true); // Add visual feedback
                // Store the start position and time for later comparison
                clickStartTimeRef.current = Date.now();
                clickStartPositionRef.current = { x: event.clientX, y: event.clientY };
                
                const clickTime = getEventTime(event);
                setIsDroging(true);
                setDrogStartTime(clickTime);
                
                // Prevent default to avoid text selection
                event.preventDefault();
                // Don't stop propagation yet - we'll determine if it's a click or drag later
            }
        },
        [getEventTime],
    );

    const onMouseMove = useCallback(
        (event) => {
            if (isDroging) {
                // Check if we've moved beyond the drag threshold
                if (clickStartPositionRef.current) {
                    const deltaX = Math.abs(event.clientX - clickStartPositionRef.current.x);
                    const deltaY = Math.abs(event.clientY - clickStartPositionRef.current.y);
                    
                    // If we've moved enough, it's definitely a drag operation
                    if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
                        // Once we confirm it's a drag, clear the click start reference
                        clickStartPositionRef.current = null;
                    }
                }
                
                if (playing) player.element.pause();
                setDrogEndTime(getEventTime(event));
            }
        },
        [isDroging, playing, player, getEventTime],
    );

    const onDocumentMouseUp = useCallback((event) => {
        const wasDragging = isDroging;
        
        if (isDroging) {
            // Check if this should be treated as a click or a drag
            const isClick = clickStartPositionRef.current && 
                          Date.now() - clickStartTimeRef.current < 300 && // Less than 300ms
                          Math.abs(event.clientX - clickStartPositionRef.current.x) < DRAG_THRESHOLD &&
                          Math.abs(event.clientY - clickStartPositionRef.current.y) < DRAG_THRESHOLD;
            
            if (!isClick && drogStartTime > 0 && drogEndTime > 0 && drogEndTime - drogStartTime >= 0.2) {
                // This was a drag operation - create subtitle
                const index = findIndex(subtitle, drogStartTime) + 1;
                const start = DT.d2t(drogStartTime);
                const end = DT.d2t(drogEndTime);
                
                console.log(`Adding subtitle at index ${index}: ${start} - ${end}`);
                addSub(
                    index,
                    newSub({
                        start,
                        end,
                        text: '',
                        text2: '',
                    }),
                );
                player.updateTime((drogStartTime + drogEndTime) / 2);
                event.preventDefault();
            } else if (isClick && metronomeRef.current) {
                // This was a click operation - seek video
                handleMetronomeSeek(event);
            }
        }
        
        // Reset all state
        setIsDroging(false);
        setDrogStartTime(0);
        setDrogEndTime(0);
        setIsClicking(false);
        clickStartTimeRef.current = Date.now();
        clickStartPositionRef.current = null;
        
        // If we were dragging, prevent this from triggering other click handlers
        if (wasDragging) {
            event.stopPropagation();
        }
    }, [isDroging, drogStartTime, drogEndTime, subtitle, addSub, newSub]);

    // Create a dedicated seek function
    const handleMetronomeSeek = useCallback((event) => {
        if (!player || !player.element) return;
        
        // Use the ref to get the DOM element
        const metronomeElement = metronomeRef.current;
        if (!metronomeElement) {
            console.error('Metronome element not found');
            return;
        }
        
        // Calculate position relative to width
        const rect = metronomeElement.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const percentPosition = clickX / rect.width;
        
        // Calculate time based on position in view
        const viewDuration = render.duration;
        const viewStartTime = render.beginTime;
        const clickTime = viewStartTime + (percentPosition * viewDuration);
        
        // Seek to that position
        if (clickTime >= 0 && clickTime <= player.element.duration) {
            console.log(`Seeking to time - Metronome: ${clickTime}`);
            player.updateTime(clickTime);
            if (waveform) {
                waveform.seek(clickTime);
            }
        }
    }, [player, render, waveform]);

    // Keep this as a fallback for direct clicks
    const handleMetronomeClick = useCallback((e) => {
        // We'll only use this as a fallback now
        // Only handle direct clicks on the metronome element itself, not child elements
        if (e.target !== metronomeRef.current) {
            return;
        }
        
        // Don't handle if we were just dragging (isDroging was true in the last 100ms)
        if (Date.now() - clickStartTimeRef.current < 1000) {
            return;
        }
        
        handleMetronomeSeek(e);
    }, [handleMetronomeSeek]);

    useEffect(() => {
        document.addEventListener('mouseup', onDocumentMouseUp);
        return () => {
            if (document !== null) {
                document.removeEventListener('mouseup', onDocumentMouseUp);
            }
        };
    }, [onDocumentMouseUp]);

    return (
        <Metronome 
            ref={metronomeRef} 
            className={`Metronome ${isClicking ? 'active' : ''}`} 
            onMouseDown={onMouseDown} 
            onMouseMove={onMouseMove} 
            onClick={handleMetronomeClick}
        >
            {/* Show a click indicator */}
            {isClicking && !isDroging && (
                <div 
                    className="click-indicator"
                    style={{
                        left: `${(clickStartPositionRef.current?.x - metronomeRef.current.getBoundingClientRect().left) || 0}px`,
                    }}
                />
            )}
            {player && !playing && drogStartTime && drogEndTime && drogEndTime > drogStartTime ? (
                <div
                    className="template"
                    style={{
                        left: render.padding * gridGap + (drogStartTime - render.beginTime) * gridGap * 10,
                        width: (drogEndTime - drogStartTime) * gridGap * 10,
                    }}
                ></div>
            ) : null}
        </Metronome>
    );
}
